import { c as _c } from "react/compiler-runtime";
import { ChainOfThoughtSearchResult, ChainOfThoughtSearchResults } from '@newfront-insurance/nova-core';
import type { ComponentType } from 'react';
import { type CoTSearchResult, getField, MAX_OUTPUT_LENGTH } from '../lib/chain-of-thought-utils';

// TODO: Export typed tool input/output interfaces from the backend so renderers
// can use concrete types instead of `unknown` + runtime field access via getField.
type CoTToolOutputRendererProps = {
  output: unknown;
};

/** Registry of custom per-tool output renderers */
const COT_TOOL_OUTPUT_RENDERERS: Record<string, ComponentType<CoTToolOutputRendererProps>> = {
  documentSearch: ({
    output
  }) => {
    const summary = getField<string>(output, 'searchSummary');
    return summary ? <p>{summary}</p> : null;
  },
  dataExplorer: ({
    output
  }) => {
    const reasoning = getField<string>(output, 'reasoning');
    return reasoning ? <p>{reasoning}</p> : null;
  }
};
type CoTToolInputRendererProps = {
  input: unknown;
};

/** Registry of custom per-tool input renderers (shown next to the step label) */
const COT_TOOL_INPUT_RENDERERS: Record<string, ComponentType<CoTToolInputRendererProps>> = {
  documentSearch: ({
    input
  }) => {
    const query = getField<string>(input, 'query');
    return query ? <span>Searching for &ldquo;{query}&rdquo;</span> : null;
  },
  processFiles: ({
    input
  }) => {
    const context = getField<string>(input, 'context');
    return context ? <span>{context}</span> : null;
  },
  dataExplorer: ({
    input
  }) => {
    const reasoning = getField<string>(input, 'lastMessageReasoning');
    return reasoning ? <span>{reasoning}</span> : null;
  }
};

/** Registry of extractors that pull search results from tool output */
const COT_TOOL_SEARCH_RESULT_EXTRACTORS: Record<string, (output: unknown) => CoTSearchResult[]> = {
  documentSearch: output => {
    const results = getField<Array<Record<string, unknown>>>(output, 'results');
    if (!Array.isArray(results)) return [];
    return results.filter((r): r is Record<string, unknown> & {
      filename: string;
    } => typeof r.filename === 'string').map(r => ({
      title: r.filename,
      link: typeof r.navigatorUrl === 'string' ? r.navigatorUrl : undefined
    }));
  },
  dataExplorer: output => {
    const rowCount = getField<number>(output, 'data', 'rowCount');
    if (rowCount == null || rowCount === 0) return [];
    return [{
      title: `${rowCount} result${rowCount === 1 ? '' : 's'} found`
    }];
  }
};

/** Renders tool input as a description for a CoT step — returns null when no custom renderer exists */
export function CoTToolInput(t0) {
  const $ = _c(3);
  const {
    toolName,
    input
  } = t0;
  const CustomRenderer = COT_TOOL_INPUT_RENDERERS[toolName];
  if (CustomRenderer) {
    let t1;
    if ($[0] !== CustomRenderer || $[1] !== input) {
      t1 = <CustomRenderer input={input} />;
      $[0] = CustomRenderer;
      $[1] = input;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  return null;
}

/** Renders search result badges from tool output — returns null when no extractor exists or no results */
export function CoTToolSearchResults(t0) {
  const $ = _c(8);
  const {
    toolName,
    output
  } = t0;
  const extractor = COT_TOOL_SEARCH_RESULT_EXTRACTORS[toolName];
  if (!extractor) {
    return null;
  }
  let T0;
  let t1;
  let t2;
  if ($[0] !== extractor || $[1] !== output) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const results = extractor(output);
      if (results.length === 0) {
        t2 = null;
        break bb0;
      }
      T0 = ChainOfThoughtSearchResults;
      t1 = results.map(_temp);
    }
    $[0] = extractor;
    $[1] = output;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[5] !== T0 || $[6] !== t1) {
    t3 = <T0>{t1}</T0>;
    $[5] = T0;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** Renders tool output in a CoT step — uses custom renderer if available, falls back to truncated string */
function _temp(result, index) {
  return result.link ? <a key={result.link} href={result.link} target="_blank" rel="noopener noreferrer"><ChainOfThoughtSearchResult>{result.title}</ChainOfThoughtSearchResult></a> : <ChainOfThoughtSearchResult key={index}>{result.title}</ChainOfThoughtSearchResult>;
}
export function CoTToolOutput(t0) {
  const $ = _c(5);
  const {
    toolName,
    output
  } = t0;
  const CustomRenderer = COT_TOOL_OUTPUT_RENDERERS[toolName];
  if (CustomRenderer) {
    let t1;
    if ($[0] !== CustomRenderer || $[1] !== output) {
      t1 = <CustomRenderer output={output} />;
      $[0] = CustomRenderer;
      $[1] = output;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (typeof output === "string") {
    const text = output.length > MAX_OUTPUT_LENGTH ? `${output.slice(0, MAX_OUTPUT_LENGTH)}…` : output;
    let t1;
    if ($[3] !== text) {
      t1 = <p>{text}</p>;
      $[3] = text;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  return null;
}